/**
 * Hook to monitor network connectivity status
 * Provides online/offline state and connection quality estimation
 */

import { useSyncExternalStore } from 'react';

// Network state store
let networkState = {
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
  connectionType: 'unknown',
  isSlowConnection: false,
  justCameOnline: false
};

let listeners = new Set();
let justCameOnlineTimer = null;

// Update connection info from Network Information API
function updateConnectionInfo() {
  if (typeof navigator !== 'undefined' && 'connection' in navigator) {
    const connection = navigator.connection;
    networkState.connectionType = connection.effectiveType || 'unknown';
    
    const slowTypes = ['slow-2g', '2g'];
    networkState.isSlowConnection = 
      slowTypes.includes(connection.effectiveType) ||
      (connection.downlink && connection.downlink < 1) ||
      (connection.rtt && connection.rtt > 500);
  }
}

// Notify all listeners of state change
function notifyListeners() {
  // Create a new object reference to trigger re-render
  networkState = { ...networkState };
  listeners.forEach(listener => listener());
}

// Handle online event
function handleOnline() {
  console.log('[Network] Connection restored');
  networkState.isOnline = true;
  networkState.justCameOnline = true;
  updateConnectionInfo();
  notifyListeners();
  
  // Clear justCameOnline flag after 5 seconds
  if (justCameOnlineTimer) {
    clearTimeout(justCameOnlineTimer);
  }
  justCameOnlineTimer = setTimeout(() => {
    networkState.justCameOnline = false;
    notifyListeners();
  }, 5000);
}

// Handle offline event
function handleOffline() {
  console.log('[Network] Connection lost');
  networkState.isOnline = false;
  networkState.justCameOnline = false;
  
  if (justCameOnlineTimer) {
    clearTimeout(justCameOnlineTimer);
  }
  notifyListeners();
}

// Handle connection change
function handleConnectionChange() {
  console.log('[Network] Connection changed');
  updateConnectionInfo();
  notifyListeners();
}

// Initialize listeners (only once)
let initialized = false;
function initializeListeners() {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
  
  if ('connection' in navigator) {
    navigator.connection.addEventListener('change', handleConnectionChange);
    updateConnectionInfo();
  }
}

// Subscribe function for useSyncExternalStore
function subscribe(listener) {
  initializeListeners();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// Get current snapshot
function getSnapshot() {
  return networkState;
}

// Server snapshot
function getServerSnapshot() {
  return {
    isOnline: true,
    connectionType: 'unknown',
    isSlowConnection: false,
    justCameOnline: false
  };
}

/**
 * Network status hook
 * @returns {Object} Network status information
 */
export function useNetworkStatus() {
  const state = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  return {
    isOnline: state.isOnline,
    isOffline: !state.isOnline,
    connectionType: state.connectionType,
    isSlowConnection: state.isSlowConnection,
    justCameOnline: state.justCameOnline,
    shouldShowOfflineWarning: !state.isOnline || state.isSlowConnection
  };
}

export default useNetworkStatus;
